import { useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useFetch } from "../../lib/useData";
import { PageTitle, Badge, TableWrap, Btn, Modal, Input, Textarea, Select } from "../../components/admin/ui";

const PAGE_SIZES = [10, 50, 100, 500, 1000];
export default function EventsAdmin() {
  const { data: events, loading, reload } = useFetch("/admin/events");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pageSize, setPageSize] = useState(10);
const [page, setPage] = useState(1);

const allRows = events || [];
const total = allRows.length;
const totalPages = Math.max(1, Math.ceil(total / pageSize));
const currentPage = Math.min(page, totalPages);
const start = (currentPage - 1) * pageSize;
const pagedRows = allRows.slice(start, start + pageSize);

  const blank = { title: "", slug: "", category: "Festival", start_date: "", end_date: "", start_time: "", end_time: "",
    description: "", cover_image: "", location: "Temple Premises", featured: false, is_utsav: false, utsav_days: [], status: "draft" };

  const openNew = () => { setForm(blank); setEditing("new"); };
  const openEdit = (row) => { setForm({ ...blank, ...row }); setEditing(row.id); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setDay = (i, k, v) => set("utsav_days", form.utsav_days.map((d, idx) => idx === i ? { ...d, [k]: v } : d));
  const addDay = () => set("utsav_days", [...(form.utsav_days || []), { day: (form.utsav_days?.length || 0) + 1, date: "", time: "", programme: "", description: "" }]);
  const rmDay = (i) => set("utsav_days", form.utsav_days.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/events", form);
      else await api.put(`/admin/events/${editing}`, form);
      toast.success("Saved"); setEditing(null); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try { await api.delete(`/admin/events/${row.id}`); toast.success("Deleted"); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageTitle title="Events & Festivals" desc="Manage festivals, special worship and the 13-day Annual Utsav">
        <Btn onClick={openNew} testid="event-add"><Plus className="w-4 h-4" /> Add Event</Btn>
      </PageTitle>
      <TableWrap>
        <thead className="bg-slate-800 text-slate-400"><tr>
          <th className="text-left px-4 py-3">Title</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Dates</th><th className="text-left px-4 py-3">Utsav</th><th className="text-left px-4 py-3">Status</th><th className="text-right px-4 py-3">Actions</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-700" data-testid="event-admin-table">
          {loading ? <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr> :
pagedRows.map((e) => (
                <tr key={e.id} className="hover:bg-slate-800/50">
                <td className="px-4 py-3 font-semibold">{e.title}</td>
                <td className="px-4 py-3 text-slate-300">{e.category}</td>
                <td className="px-4 py-3 text-slate-400">{e.start_date}</td>
                <td className="px-4 py-3">{e.is_utsav ? `${e.utsav_days?.length || 0} days` : "—"}</td>
                <td className="px-4 py-3"><Badge status={e.status} /></td>
                <td className="px-4 py-3"><div className="flex justify-end gap-1">
                  <button onClick={() => openEdit(e)} className="p-2 rounded-lg hover:bg-slate-700 text-blue-400" data-testid={`event-edit-${e.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(e)} className="p-2 rounded-lg hover:bg-slate-700 text-red-400" data-testid={`event-delete-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                </div></td>
              </tr>
            ))}
        </tbody>
      </TableWrap>

{!loading && total > 0 && (
  <div className="flex flex-wrap items-center justify-between gap-3 mt-4 text-sm text-slate-300">
    <div className="flex items-center gap-2">
      <span>Show</span>
      <select
        value={pageSize}
        onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
        className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-slate-200"
        data-testid="event-page-size"
      >
        {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <span>per page</span>
    </div>

    <span className="text-slate-400">
      {start + 1}–{Math.min(start + pageSize, total)} of {total}
    </span>

    <div className="flex items-center gap-2">
      <button
        onClick={() => setPage(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >Prev</button>
      <span>{currentPage} / {totalPages}</span>
      <button
        onClick={() => setPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
      >Next</button>
    </div>
  </div>
)}
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "Add Event" : "Edit Event"} wide>
        <div className="grid sm:grid-cols-2 gap-4">
          <Input label="Title *" value={form.title} onChange={(v) => set("title", v)} testid="event-f-title" />
          <Input label="Slug" value={form.slug} onChange={(v) => set("slug", v)} />
          <Select label="Category" value={form.category} onChange={(v) => set("category", v)} options={["Festival", "Annual Utsav", "Special Worship", "Bhajan", "Kirtan", "Discourse"]} />
          <Input label="Location" value={form.location} onChange={(v) => set("location", v)} />
          <Input label="Start Date" value={form.start_date} onChange={(v) => set("start_date", v)} testid="event-f-start" />
          <Input label="End Date" value={form.end_date} onChange={(v) => set("end_date", v)} />
          <Input label="Start Time" value={form.start_time} onChange={(v) => set("start_time", v)} />
          <Input label="End Time" value={form.end_time} onChange={(v) => set("end_time", v)} />
          <div className="sm:col-span-2"><Input label="Cover Image URL" value={form.cover_image} onChange={(v) => set("cover_image", v)} /></div>
          <div className="sm:col-span-2"><Textarea label="Description" value={form.description} onChange={(v) => set("description", v)} rows={4} /></div>
          <Select label="Featured" value={String(form.featured)} onChange={(v) => set("featured", v === "true")} options={[{ value: "false", label: "No" }, { value: "true", label: "Yes" }]} />
          <Select label="Is Annual Utsav" value={String(form.is_utsav)} onChange={(v) => set("is_utsav", v === "true")} options={[{ value: "false", label: "No" }, { value: "true", label: "Yes (13-day programme)" }]} />
          <Select label="Status" value={form.status} onChange={(v) => set("status", v)} options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} />

          {form.is_utsav && (
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-slate-300">Utsav Day Programmes</label>
                <Btn variant="outline" onClick={addDay} testid="utsav-add-day"><Plus className="w-3 h-3" /> Day</Btn>
              </div>
              <div className="space-y-3">
                {(form.utsav_days || []).map((d, i) => (
                  <div key={i} className="bg-slate-900 rounded-lg p-3 relative">
                    <button onClick={() => rmDay(i)} className="absolute top-2 right-2 text-red-400"><X className="w-4 h-4" /></button>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <input value={d.day} onChange={(e) => setDay(i, "day", Number(e.target.value))} placeholder="Day #" type="number" className="rounded bg-slate-800 border border-slate-600 px-2 py-1.5 text-sm" />
                      <input value={d.date} onChange={(e) => setDay(i, "date", e.target.value)} placeholder="Date" className="rounded bg-slate-800 border border-slate-600 px-2 py-1.5 text-sm" />
                      <input value={d.time} onChange={(e) => setDay(i, "time", e.target.value)} placeholder="Time" className="rounded bg-slate-800 border border-slate-600 px-2 py-1.5 text-sm" />
                    </div>
                    <input value={d.programme} onChange={(e) => setDay(i, "programme", e.target.value)} placeholder="Programme" className="w-full rounded bg-slate-800 border border-slate-600 px-2 py-1.5 text-sm mb-2" />
                    <input value={d.description} onChange={(e) => setDay(i, "description", e.target.value)} placeholder="Description" className="w-full rounded bg-slate-800 border border-slate-600 px-2 py-1.5 text-sm" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Btn variant="outline" onClick={() => setEditing(null)}>Cancel</Btn>
          <Btn onClick={save} disabled={saving} testid="event-save">{saving ? "Saving…" : "Save Event"}</Btn>
        </div>
      </Modal>
    </div>
  );
}
